import React, { useState, useEffect, useCallback } from "react";
import api from "../../utils/api";
import {
  Plus, Pencil, Trash2, CheckCircle, AlertCircle,
  X, Search, Building2, Phone, Mail, Globe, CreditCard
} from "lucide-react";

const PAYMENT_TERMS = ["Net 7", "Net 14", "Net 30", "Net 60", "Net 90", "Due on Receipt"];
const CURRENCIES = ["AUD", "USD", "EUR", "GBP", "SGD", "NZD"];
const STATUS_CFG = {
  active:   { cls: "bg-emerald-100 text-emerald-700", label: "Active" },
  inactive: { cls: "bg-slate-100 text-slate-500",    label: "Inactive" },
};

const EMPTY_FORM = {
  name: "", contactName: "", email: "", phone: "",
  address: "", country: "Australia", currency: "AUD", paymentTerms: "Net 30",
};

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | "create" | "edit" | "delete"
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const flash = (msg, type = "success") => {
    if (type === "success") { setSuccess(msg); setTimeout(() => setSuccess(""), 4000); }
    else { setError(msg); setTimeout(() => setError(""), 5000); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/suppliers");
      setSuppliers(res.data.suppliers || []);
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      suppliers.filter(s =>
        s.name?.toLowerCase().includes(q) ||
        s.code?.toLowerCase().includes(q) ||
        s.contactName?.toLowerCase().includes(q) ||
        s.email?.toLowerCase().includes(q)
      )
    );
  }, [search, suppliers]);

  const openCreate = () => { setForm(EMPTY_FORM); setModal("create"); };
  const openEdit = (s) => {
    setSelected(s);
    setForm({
      name: s.name || "", contactName: s.contactName || "", email: s.email || "",
      phone: s.phone || "", address: s.address || "", country: s.country || "Australia",
      currency: s.currency || "AUD", paymentTerms: s.paymentTerms || "Net 30",
    });
    setModal("edit");
  };
  const openDelete = (s) => { setSelected(s); setModal("delete"); };
  const closeModal = () => { setModal(null); setSelected(null); setError(""); };

  const handleSave = async () => {
    if (!form.name.trim()) { flash("Supplier name is required", "error"); return; }
    setSaving(true);
    try {
      if (modal === "create") {
        await api.post("/api/suppliers", form);
        flash("Supplier created.");
      } else {
        await api.put(`/api/suppliers/${selected.id}`, form);
        flash("Supplier updated.");
      }
      closeModal();
      load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/api/suppliers/${selected.id}`);
      flash("Supplier deleted.");
      closeModal();
      load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (s) => {
    const newStatus = s.status === "active" ? "inactive" : "active";
    try {
      await api.put(`/api/suppliers/${s.id}`, { status: newStatus });
      load();
    } catch (e) {
      flash(e.response?.data?.error || e.message, "error");
    }
  };

  return (
    <div className="max-w-6xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-100 rounded-xl"><Building2 size={24} className="text-blue-600" /></div>
          <div>
            <h1 className="text-2xl font-bold text-navy-900">Suppliers</h1>
            <p className="text-slate-500 text-sm">Manage your vendor & supplier directory</p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-5 py-2.5 bg-navy-900 text-white rounded-xl text-sm font-bold hover:bg-navy-800 transition-colors"
        >
          <Plus size={16} /> Add Supplier
        </button>
      </div>

      {/* Alerts */}
      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2"><AlertCircle size={16}/>{error}</div>}
      {success && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm flex items-center gap-2"><CheckCircle size={16}/>{success}</div>}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: "Total Suppliers", value: suppliers.length },
          { label: "Active",          value: suppliers.filter(s => s.status === "active").length },
          { label: "Inactive",        value: suppliers.filter(s => s.status !== "active").length },
        ].map((m, i) => (
          <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{m.label}</p>
            <p className="text-2xl font-bold text-navy-900">{m.value}</p>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-4 flex items-center gap-3">
        <Search size={16} className="text-slate-400" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, code, contact, or email..."
          className="flex-1 text-sm focus:outline-none text-slate-700 placeholder-slate-400"
        />
        {search && <button onClick={() => setSearch("")}><X size={15} className="text-slate-400"/></button>}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-slate-400 text-sm">Loading suppliers...</div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-slate-400 text-sm">
            {search ? "No suppliers match your search." : "No suppliers yet. Add your first one."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100 bg-slate-50">
                <th className="text-left px-6 py-4">Supplier</th>
                <th className="text-left px-4 py-4">Contact</th>
                <th className="text-left px-4 py-4">Terms</th>
                <th className="text-left px-4 py-4">Currency</th>
                <th className="text-left px-4 py-4">Status</th>
                <th className="text-left px-4 py-4">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filtered.map(s => {
                const stCfg = STATUS_CFG[s.status] || STATUS_CFG.inactive;
                return (
                  <tr key={s.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-semibold text-navy-900">{s.name}</p>
                      <p className="text-xs text-slate-400 font-mono">{s.code}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-slate-700">{s.contactName || "—"}</p>
                      <p className="text-xs text-slate-400">{s.email || ""}</p>
                    </td>
                    <td className="px-4 py-4 text-slate-600">{s.paymentTerms}</td>
                    <td className="px-4 py-4 font-mono text-slate-600 text-xs">{s.currency}</td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => handleToggleStatus(s)}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider cursor-pointer ${stCfg.cls}`}
                      >
                        {stCfg.label}
                      </button>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEdit(s)} className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors">
                          <Pencil size={14}/>
                        </button>
                        <button onClick={() => openDelete(s)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors">
                          <Trash2 size={14}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create / Edit Modal */}
      {(modal === "create" || modal === "edit") && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
              <h2 className="font-bold text-navy-900 text-lg">
                {modal === "create" ? "Add Supplier" : "Edit Supplier"}
              </h2>
              <button onClick={closeModal}><X size={18} className="text-slate-400"/></button>
            </div>
            <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
              {error && <p className="text-xs text-red-500">{error}</p>}

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Supplier Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                  className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  placeholder="e.g. Acme Freight Co." />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Contact Name</label>
                  <input value={form.contactName} onChange={e => setForm(f => ({...f, contactName: e.target.value}))}
                    className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                    placeholder="John Smith" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Phone</label>
                  <input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))}
                    className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                    placeholder="+61 2 0000 0000" />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Email</label>
                <input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))}
                  className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  placeholder="supplier@example.com" />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Address</label>
                <input value={form.address} onChange={e => setForm(f => ({...f, address: e.target.value}))}
                  className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  placeholder="123 Street, City, State" />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Country</label>
                  <input value={form.country} onChange={e => setForm(f => ({...f, country: e.target.value}))}
                    className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Currency</label>
                  <select value={form.currency} onChange={e => setForm(f => ({...f, currency: e.target.value}))}
                    className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300">
                    {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Payment Terms</label>
                  <select value={form.paymentTerms} onChange={e => setForm(f => ({...f, paymentTerms: e.target.value}))}
                    className="mt-1 w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300">
                    {PAYMENT_TERMS.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="px-6 pb-6 pt-4 border-t border-slate-100 flex justify-end gap-3">
              <button onClick={closeModal} className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={handleSave} disabled={saving}
                className="px-5 py-2.5 rounded-xl bg-navy-900 text-white text-sm font-bold hover:bg-navy-800 disabled:opacity-50">
                {saving ? "Saving..." : modal === "create" ? "Add Supplier" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {modal === "delete" && selected && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h2 className="font-bold text-navy-900 text-lg mb-2">Delete Supplier</h2>
            <p className="text-sm text-slate-500 mb-6">
              Delete <span className="font-semibold text-navy-900">{selected.name}</span> ({selected.code})? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={closeModal} className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button onClick={handleDelete} disabled={saving}
                className="px-5 py-2.5 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-50">
                {saving ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
